import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { motion, springs, PageTransition, Stagger, Pressable, FadeIn } from '../lib/motion';

const DISCIPLINES = [
  {
    key: 'nursing',
    label: 'Nursing',
    subtitle: 'Critical thinking, case studies, pharmacology, and clinical stories',
    icon: 'nursing',
    itemCount: 150,
    tile: 'bg-gradient-to-br from-teal-400 to-emerald-500 text-white',
    glow: 'from-teal-400/20 to-emerald-500/20',
    hairline: 'bg-gradient-to-r from-squishy-teal to-emerald-500',
    count: 'bg-teal-50 dark:bg-teal-500/15 text-squishy-tealDark dark:text-teal-300',
    available: true,
  },
  {
    key: 'medicine',
    label: 'Medicine',
    subtitle: 'Clinical cases, diagnostics, pharmacology, and emergencies',
    icon: 'medicine',
    itemCount: 100,
    tile: 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white',
    glow: 'from-blue-500/20 to-indigo-600/20',
    hairline: 'bg-gradient-to-r from-blue-500 to-indigo-600',
    count: 'bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300',
    available: true,
  },
  {
    key: 'law',
    label: 'Law',
    subtitle: 'Case briefs, doctrine, issue-spotting, and exam prep',
    icon: 'law',
    itemCount: 100,
    tile: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white',
    glow: 'from-amber-400/20 to-orange-500/20',
    hairline: 'bg-gradient-to-r from-squishy-gold to-orange-500',
    count: 'bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300',
    available: true,
  },
] as const;

const ICON_PATHS: Record<string, string> = {
  // Heart: clinical care
  nursing:
    'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  // Medical cross
  medicine:
    'M10.5 4.5h3a.75.75 0 01.75.75V9h3.75a.75.75 0 01.75.75v4.5a.75.75 0 01-.75.75H14.25v3.75a.75.75 0 01-.75.75h-3a.75.75 0 01-.75-.75V15H6a.75.75 0 01-.75-.75v-4.5A.75.75 0 016 9h3.75V5.25a.75.75 0 01.75-.75z',
  // Scales of justice
  law:
    'M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z',
};

export function LearnPage() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <PageTransition className="max-w-2xl mx-auto pb-20 space-y-5">
      <FadeIn>
        <h1 className="font-display text-2xl font-semibold text-squishy-dark">Learn</h1>
        <p className="text-sm text-slate-500 mt-1">Deep-dive content to build real understanding</p>
      </FadeIn>

      <Stagger className="space-y-3.5">
        {DISCIPLINES.map((d) => (
          <Stagger.Item key={d.key}>
            <Pressable
              onClick={() => d.available && navigate(`/learn/${d.key}`)}
              disabled={!d.available}
              aria-label={`${d.label}: ${d.subtitle}`}
              whileHover={reduce || !d.available ? undefined : { y: -3 }}
              className={`group relative w-full text-left rounded-3xl overflow-hidden bg-white border border-slate-200/80 shadow-card transition-shadow ${
                d.available ? 'hover:shadow-pop cursor-pointer' : 'opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Soft color wash that warms up on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${d.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} aria-hidden />
              <div className={`h-1.5 ${d.hairline}`} />
              <div className="relative p-5">
                {!d.available && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full uppercase tracking-wide">
                    Coming Soon
                  </span>
                )}
                <div className="flex items-start gap-4">
                  <motion.span
                    whileHover={reduce ? undefined : { rotate: -6, scale: 1.06 }}
                    transition={springs.bouncy}
                    className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${d.tile}`}
                  >
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[d.icon]} />
                    </svg>
                  </motion.span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-semibold text-squishy-dark leading-tight">{d.label}</h2>
                    <p className="text-sm text-slate-500 mt-1 leading-snug">{d.subtitle}</p>
                    {d.available && d.itemCount > 0 && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${d.count}`}>{d.itemCount} items</span>
                        <span className="text-xs text-slate-400">Long-form, self-paced</span>
                      </div>
                    )}
                  </div>
                  {d.available && (
                    <span className="shrink-0 mt-1 text-slate-300 transition-all duration-200 group-hover:text-squishy-primary group-hover:translate-x-0.5" aria-hidden>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            </Pressable>
          </Stagger.Item>
        ))}
      </Stagger>
    </PageTransition>
  );
}
